import { getWallPath } from "building-generation/elements/walls/wall";
import type { DimensionGeometryProps } from "building-types/dimensions/dimensionTypes";
import type { EdgeDimensionBehavior } from "building-types/edgeBehavior/edgeDimensionBehavior";
import type { HalfEdgeAssociationTable } from "building-types/planarGraphs/planarGraphAssociationTypes";
import type { EdgeData } from "building-types/provenance/halfEdgeDataTypes";
import type { RoomSet } from "building-types/rooms/roomTraversalTypes";
import type { Room } from "building-types/rooms/roomTypes";
import type { WallSystem } from "building-types/walls/wallSytemTypes";
import type { Table } from "common/data/table";
import { getDimLabelPosition } from "doc/dimensions";
import type { CollisionType } from "doc/plans/interiorLabelObstacle";
import { polyLine2EdgeDataNewFromHalfEdges } from "doc/provenance/halfEdgeDataTools";
import { aabb2AddPoint } from "geom/aabb2";
import { aabb2New } from "geom/aabb2Types";
import type { AABB2 } from "geom/aabb2Types";
import { DIST_EPSILON, Line2Side } from "geom/core";
import { intersectRay2Ray2 } from "geom/intersect";
import { line2Len, line2PerpDistance, line2ProjectPt, line2Side, line2Tangent } from "geom/line2";
import { planarGraphHalfEdgeEnd, planarGraphHalfEdgeStart } from "geom/planarGraph";
import {
  polyLine2ByTwoPoints,
  polyLine2EdgeEval,
  polyLine2EdgeLength,
  polyLine2EdgeTangent,
  polyLine2IsAxisAlignedEdge,
  polyLine2IsHorizontalEdge,
  polyLine2Length,
  polyLine2Reverse,
  polyLine2Vertex,
} from "geom/polyLine2";
import type { PolyLine2 } from "geom/polyLine2Types";
import { ray2DotProject, ray2Eval } from "geom/ray2";
import { vec2Dot, vec2IsInEdgeBounds } from "geom/vec2";
import type { InteriorDimensionWallIndex } from "./buildCompositeRoomSetWallIndexContext";

export const ALIGNED_WALL_EPSILON = 0.0001;

interface LinesByRoomGroup {
  diagonalLines: {
    dimension1: PolyLine2;
    dimension2: PolyLine2;
    halfEdges: number[];
  }[];
  lines: {
    dimensionLine: PolyLine2;
    horizontalEdge: boolean;
    rangeStartX: number;
    rangeStartY: number;
    rangeEndX: number;
    rangeEndY: number;
    startHalfEdge: number;
    endHalfEdge: number;
  }[];
}

type ResolveHalfEdgeLine = (halfEdge: number) => [number, number, number, number] | undefined;

function getInteriorDimensionLinesByRoomGroup(
  roomSet: RoomSet,
  rooms: Table<Room>,
  levelRooms: Room[],
  wallIndex: InteriorDimensionWallIndex,
  resolveHalfEdgeLine?: ResolveHalfEdgeLine,
): LinesByRoomGroup {
  const ret: LinesByRoomGroup = {
    diagonalLines: [],
    lines: [],
  };
  const getHalfEdgeLine = (halfEdge: number): [number, number, number, number] | undefined => {
    if (resolveHalfEdgeLine) {
      return resolveHalfEdgeLine(halfEdge);
    }

    const planarGraph = wallIndex.planarGraph?.planarGraph;
    if (!planarGraph) {
      return undefined;
    }

    const [ax, ay] = planarGraphHalfEdgeStart(planarGraph, halfEdge);
    const [bx, by] = planarGraphHalfEdgeEnd(planarGraph, halfEdge);
    return [ax, ay, bx, by];
  };

  // build the wall edges for this room set
  const solidStrips = wallIndex.solidWallStripsInRoomSet(roomSet, rooms, levelRooms, true, true);
  const { segments: wallEdges, segmentHalfEdges: wallHalfEdges } =
    wallIndex.mergeCollinearWallStripsReturnHalfEdges(solidStrips);

  // iterate the wall edges
  const existingDimensions: ExistingDimension[][] = [];
  for (let s = 0, ls = wallEdges.length; s < ls; s++) {
    const wallEdge = wallEdges[s];
    const halfEdges = wallHalfEdges[s];
    const nextWallEdge = wallEdges[(s + +1) % ls];
    const prevWallEdge = wallEdges[(s + ls - 1) % ls];

    // if this is not an aligned edge
    if (!polyLine2IsAxisAlignedEdge(wallEdge, 0, ALIGNED_WALL_EPSILON)) {
      // check if it's a convex diagonal wall
      const [prevControlPointX, prevControlPointY] = polyLine2Vertex(prevWallEdge, 0);
      const [nextControlPointX, nextControlPointY] = polyLine2Vertex(nextWallEdge, 1);

      const [startX, startY] = polyLine2Vertex(wallEdge, 0);
      const [endX, endY] = polyLine2Vertex(wallEdge, 1);
      const [tangentX, tangentY] = polyLine2EdgeTangent(wallEdge, 0);

      const side1 = line2Side(startX, startY, endX, endY, prevControlPointX, prevControlPointY);
      const side2 = line2Side(startX, startY, endX, endY, nextControlPointX, nextControlPointY);

      // if it's a convex diagonal wall
      if (
        side1 !== Line2Side.LEFT &&
        side2 !== Line2Side.LEFT &&
        (side1 === Line2Side.RIGHT || side2 === Line2Side.RIGHT)
      ) {
        // generate the start and the end of the wall
        const [prevTangentX, prevTangentY] = polyLine2EdgeTangent(prevWallEdge, 0);
        const [nextTangentX, nextTangentY] = polyLine2EdgeTangent(nextWallEdge, 0);

        const [prevX, prevY] = polyLine2Vertex(prevWallEdge, 0);
        const [nextX, nextY] = polyLine2Vertex(nextWallEdge, 0);

        const intersectionA = intersectRay2Ray2(
          prevX,
          prevY,
          prevTangentX,
          prevTangentY,
          startX,
          startY,
          tangentX,
          tangentY,
        );
        const intersectionB = intersectRay2Ray2(
          startX,
          startY,
          tangentX,
          tangentY,
          nextX,
          nextY,
          nextTangentX,
          nextTangentY,
        );

        if (intersectionA && intersectionB) {
          const { x: ax, y: ay } = intersectionA;
          const { x: cx, y: cy } = intersectionB;

          // split it into 2 axis aligned dimensions
          const t = vec2Dot(prevTangentX, prevTangentY, cx - ax, cy - ay);
          const [bx, by] = ray2Eval(ax, ay, prevTangentX, prevTangentY, t);

          const dimension1 = polyLine2ByTwoPoints(ax, ay, bx, by);
          const dimension2 = polyLine2ByTwoPoints(bx, by, cx, cy);

          ret.diagonalLines.push({ dimension1, dimension2, halfEdges });
        }
      }
      continue;
    }

    const startHalfEdgeCandidates = wallHalfEdges[s];
    const horizontalEdge = polyLine2IsHorizontalEdge(wallEdge, 0, ALIGNED_WALL_EPSILON);

    // get opposited wall edges
    const oppIndices = oppositeWallEdges(wallEdges, s);

    // iterate opposite wall edges
    for (let o = 0, lo = oppIndices.length; o < lo; o++) {
      const { index: oppIndex, startT, endT, t } = oppIndices[o];
      const oppWallEdge = wallEdges[oppIndex];

      // only for axis aligned opposite edges
      if (!polyLine2IsAxisAlignedEdge(oppWallEdge, 0, ALIGNED_WALL_EPSILON)) {
        continue;
      }

      // generate the dimensions line
      const [x0, y0] = polyLine2EdgeEval(wallEdge, 0, t, false);
      const [rangeStartX, rangeStartY] = polyLine2EdgeEval(wallEdge, 0, startT, false);
      const [rangeEndX, rangeEndY] = polyLine2EdgeEval(wallEdge, 0, endT, false);
      const [minOppX, minOppY] = polyLine2EdgeEval(oppWallEdge, 0, 0.5, true);

      const x1 = horizontalEdge ? x0 : minOppX;
      const y1 = horizontalEdge ? minOppY : y0;

      // check for duplicated dimensions
      if (isExistingDimension(existingDimensions, x0, y0, x1, y1)) {
        continue;
      }

      const endHalfEdgeCandidates = wallHalfEdges[oppIndex];

      let startHalfEdge = -1;
      let minDist = Number.MAX_VALUE;

      for (let i = 0; i < 2 && startHalfEdge === -1; i++) {
        for (const halfEdge of startHalfEdgeCandidates) {
          const halfEdgeLine = getHalfEdgeLine(halfEdge);
          if (!halfEdgeLine) {
            continue;
          }
          const [ax, ay, bx, by] = halfEdgeLine;

          const dist = line2PerpDistance(ax, ay, bx, by, x0, y0);
          const [xProjected, yProjected] = line2ProjectPt(ax, ay, bx, by, x0, y0);
          const isEdgeBound = i === 0 ? vec2IsInEdgeBounds(xProjected, yProjected, ax, ay, bx, by) : true;
          if (dist < minDist && isEdgeBound) {
            minDist = dist;
            startHalfEdge = halfEdge;
          }
        }
      }

      let endHalfEdge = -1;
      minDist = Number.MAX_VALUE;

      for (let i = 0; i < 2 && endHalfEdge === -1; i++) {
        for (const halfEdge of endHalfEdgeCandidates) {
          const halfEdgeLine = getHalfEdgeLine(halfEdge);
          if (!halfEdgeLine) {
            continue;
          }
          const [ax, ay, bx, by] = halfEdgeLine;

          const dist = line2PerpDistance(ax, ay, bx, by, x0, y0);
          const [xProjected, yProjected] = line2ProjectPt(ax, ay, bx, by, x0, y0);
          const isEdgeBound = i === 0 ? vec2IsInEdgeBounds(xProjected, yProjected, ax, ay, bx, by) : true;
          if (dist < minDist && isEdgeBound) {
            minDist = dist;
            endHalfEdge = halfEdge;
          }
        }
      }

      let dimensionLine = polyLine2ByTwoPoints(x0, y0, x1, y1);
      if ((!horizontalEdge && x1 < x0) || (horizontalEdge && y1 > y0)) {
        dimensionLine = polyLine2Reverse(dimensionLine);
      }

      ret.lines.push({
        dimensionLine,
        horizontalEdge,
        rangeStartX,
        rangeStartY,
        rangeEndX,
        rangeEndY,
        startHalfEdge,
        endHalfEdge,
      });

      // add to the existing dimensions
      addToExistingDimensions(existingDimensions, x0, y0, x1, y1);
    }
  }

  return ret;
}

export type AddInteriorLabel = (
  collisionType: CollisionType,
  rangeAABB: AABB2,
  label: string,
  fontSize: number,
  x: number,
  y: number,
  labelBorder: number,
  rotationRads: number,
  dimensionLine?: PolyLine2,
  baseLine?: PolyLine2,
  appendToPrevGroup?: boolean,
  mass?: number,
  moveOnDimeLine?: boolean,
) => void;

interface AddInteriorDimensionsByRoomGroup {
  roomSet: RoomSet;
  rooms: Table<Room>;
  levelRooms: Room[];
  wallIndex: InteriorDimensionWallIndex;
  dims: DimensionGeometryProps[];
  levelId: string;
  compositeLevelId?: string;
  wallLevelId?: string;
  diagonalDimL: number;
  dimExt: number;
  dimensionBehavior: HalfEdgeAssociationTable<EdgeDimensionBehavior>;
  wallTable: Table<Pick<WallSystem, "walls" | "halfEdgeToWall">>;
  resolveHalfEdgeData?: (halfEdges: number[]) => EdgeData[];
  resolveHalfEdgeLine?: ResolveHalfEdgeLine;
}

export function addInteriorDimensionsByRoomGroup({
  roomSet,
  rooms,
  levelRooms,
  wallIndex,
  dims,
  diagonalDimL,
  dimExt,
  dimensionBehavior,
  levelId,
  compositeLevelId,
  wallLevelId,
  wallTable,
  resolveHalfEdgeData,
  resolveHalfEdgeLine,
}: AddInteriorDimensionsByRoomGroup) {
  const dimLines = getInteriorDimensionLinesByRoomGroup(roomSet, rooms, levelRooms, wallIndex, resolveHalfEdgeLine);
  const sourceLevelId = compositeLevelId ? wallLevelId ?? levelId : levelId;
  const walls = wallTable[sourceLevelId];

  if (!walls) {
    return;
  }

  const roomIsInRoomSet = (roomId: string) => {
    return roomSet.find((x) => x.id === roomId);
  };

  const halfEdgesAreDisabledViaRoom = (startHalfEdge: number, endHalfEdge: number) => {
    const startWallIndex = walls.halfEdgeToWall[startHalfEdge];
    const startWall = walls.walls[startWallIndex];
    if (!startWall) {
      return false;
    }
    const startWallPath = getWallPath(startWall);
    const startLeftRoom = startWallPath.leftRoom;
    const startRightRoom = startWallPath.rightRoom;

    const endWallIndex = walls.halfEdgeToWall[endHalfEdge];
    const endWall = walls.walls[endWallIndex];
    if (!endWall) {
      return false;
    }
    const endWallPath = getWallPath(endWall);
    const endLeftRoom = endWallPath.leftRoom;
    const endRightRoom = endWallPath.rightRoom;

    if (
      startLeftRoom &&
      endLeftRoom &&
      roomIsInRoomSet(startLeftRoom.id) &&
      roomIsInRoomSet(endLeftRoom.id) &&
      startLeftRoom.attributes.disableInteriorDimensions &&
      endLeftRoom.attributes.disableInteriorDimensions
    ) {
      return true;
    }

    if (
      startRightRoom &&
      endRightRoom &&
      roomIsInRoomSet(startRightRoom.id) &&
      roomIsInRoomSet(endRightRoom.id) &&
      startRightRoom.attributes.disableInteriorDimensions &&
      endRightRoom.attributes.disableInteriorDimensions
    ) {
      return true;
    }

    if (
      startLeftRoom &&
      endRightRoom &&
      roomIsInRoomSet(startLeftRoom.id) &&
      roomIsInRoomSet(endRightRoom.id) &&
      startLeftRoom.attributes.disableInteriorDimensions &&
      endRightRoom.attributes.disableInteriorDimensions
    ) {
      return true;
    }

    if (
      startRightRoom &&
      endLeftRoom &&
      roomIsInRoomSet(startRightRoom.id) &&
      roomIsInRoomSet(endLeftRoom.id) &&
      startRightRoom.attributes.disableInteriorDimensions &&
      endLeftRoom.attributes.disableInteriorDimensions
    ) {
      return true;
    }
    return false;
  };

  for (const diag of dimLines.diagonalLines) {
    const { dimension1, dimension2, halfEdges } = diag;

    if (halfEdges.find((x) => dimensionBehavior[x]?.find((y) => y.item.disableDiagonalAuto))) {
      continue;
    }

    const halfEdgeData = resolveHalfEdgeData
      ? resolveHalfEdgeData(halfEdges)
      : polyLine2EdgeDataNewFromHalfEdges(sourceLevelId, halfEdges);

    const dim1: DimensionGeometryProps = {
      pl: dimension1,
      baseLineOffset: diagonalDimL,
      dimExt,
      endExt: Math.max(polyLine2Length(dimension2) - diagonalDimL / 2, 0),
      halfEdges: halfEdgeData,
    };

    dims.push(dim1);

    const dim2: DimensionGeometryProps = {
      pl: dimension2,
      baseLineOffset: diagonalDimL,
      dimExt,
      startExt: Math.max(polyLine2Length(dimension1) - diagonalDimL / 2, 0),
      halfEdges: halfEdgeData,
    };

    dims.push(dim2);
  }

  for (const line of dimLines.lines) {
    const {
      dimensionLine,
      horizontalEdge,
      rangeStartX,
      rangeStartY,
      rangeEndX,
      rangeEndY,
      startHalfEdge,
      endHalfEdge,
    } = line;
    const [x0, y0, x1, y1] = dimensionLine;

    // if this dimension line terminates on an edge that is disabled, skip it

    if (
      dimensionBehavior[startHalfEdge]?.find((x) => x.item.disableInteriorAuto) ||
      dimensionBehavior[endHalfEdge]?.find((x) => x.item.disableInteriorAuto)
    ) {
      continue;
    }

    if (halfEdgesAreDisabledViaRoom(startHalfEdge, endHalfEdge)) {
      continue;
    }

    // add dimension label
    const [x, y] = getDimLabelPosition(dimensionLine);
    const rangeAABB = aabb2New(
      Number.POSITIVE_INFINITY,
      Number.POSITIVE_INFINITY,
      Number.NEGATIVE_INFINITY,
      Number.NEGATIVE_INFINITY,
    );

    if (horizontalEdge) {
      aabb2AddPoint(rangeAABB, rangeStartX, y0);
      aabb2AddPoint(rangeAABB, rangeEndX, y1);
    } else {
      aabb2AddPoint(rangeAABB, x0, rangeStartY);
      aabb2AddPoint(rangeAABB, x1, rangeEndY);
    }

    const halfEdgeData = resolveHalfEdgeData
      ? resolveHalfEdgeData([startHalfEdge, endHalfEdge])
      : polyLine2EdgeDataNewFromHalfEdges(sourceLevelId, [startHalfEdge, endHalfEdge]);

    const dim: DimensionGeometryProps = {
      pl: dimensionLine,
      dimExt,
      labelPosX: x,
      labelPosY: y,
      rangeAABB,
      halfEdges: halfEdgeData,
    };

    dims.push(dim);
  }
}

function addToExistingDimensions(
  existingDimensions: ExistingDimension[][],
  x0: number,
  y0: number,
  x1: number,
  y1: number,
) {
  const dimIndex = getExistingDimensionIndex(x0, y0, x1, y1);

  if (!existingDimensions[dimIndex]) {
    existingDimensions[dimIndex] = [];
  }

  existingDimensions[dimIndex].push(existingDimensionNew(x0, y0, x1, y1));
}

function isExistingDimension(
  existingDimensions: ExistingDimension[][],
  x0: number,
  y0: number,
  x1: number,
  y1: number,
): boolean {
  const dimIndex = getExistingDimensionIndex(x0, y0, x1, y1);
  const dimLength = line2Len(x0, y0, x1, y1);

  const [dimDirX, dimDirY] = line2Tangent(x0, y0, x1, y1);

  const addedDim = existingDimensions[dimIndex];
  if (addedDim) {
    for (let d = 0, ld = addedDim.length; d < ld; d++) {
      const { ax, ay, bx, by } = addedDim[d];

      const dot0 = ray2DotProject(x0, y0, dimDirX, dimDirY, ax, ay);
      const dot1 = ray2DotProject(x0, y0, dimDirX, dimDirY, bx, by);

      if (
        (Math.abs(dot0) < DIST_EPSILON && Math.abs(dimLength - dot1) < DIST_EPSILON) ||
        (Math.abs(dimLength - dot0) < DIST_EPSILON && Math.abs(dot1) < DIST_EPSILON)
      ) {
        return true;
      }
    }
  }

  return false;
}

interface ExistingDimension {
  ax: number;
  ay: number;
  bx: number;
  by: number;
}

function existingDimensionNew(ax: number, ay: number, bx: number, by: number): ExistingDimension {
  return {
    ax,
    ay,
    bx,
    by,
  };
}

function getExistingDimensionIndex(x0: number, y0: number, x1: number, y1: number): number {
  const dimLength = line2Len(x0, y0, x1, y1);
  return Math.round(dimLength * 100);
}

interface OppositeWallEdge {
  index: number;
  startT: number;
  endT: number;
  t: number;
  dist: number;
}

function oppositeWallEdgeNew(index: number, startT: number, endT: number, t: number, dist: number): OppositeWallEdge {
  return {
    index,
    startT,
    endT,
    t,
    dist,
  };
}

function oppositeWallEdges(edges: PolyLine2[], edgeIndex: number): OppositeWallEdge[] {
  const edgeCount = edges.length;

  const ret: OppositeWallEdge[] = [];

  const currentEdge = edges[edgeIndex];
  const currentLen = polyLine2EdgeLength(currentEdge, 0);
  const originX = currentEdge[0];
  const originY = currentEdge[1];
  const [dirX, dirY] = polyLine2EdgeTangent(currentEdge, 0);

  const biggerEpsilon = 10 * DIST_EPSILON;
  const minOverlap = 10;
  const idealDimLineOffset = 20;

  // iterate the edges
  for (let i = 0; i < edgeCount; i++) {
    // skip self
    if (i === edgeIndex) {
      continue;
    }

    // look for opposite edges
    const oppEdge = edges[i];
    const [oppDirX, oppDirY] = polyLine2EdgeTangent(oppEdge, 0);
    const oppLen = polyLine2EdgeLength(oppEdge, 0);
    const [x0, y0] = polyLine2EdgeEval(oppEdge, 0, biggerEpsilon);
    const [x1, y1] = polyLine2EdgeEval(oppEdge, 0, oppLen - biggerEpsilon);

    if (
      vec2Dot(dirX, dirY, oppDirX, oppDirY) > -DIST_EPSILON ||
      line2Side(x0, y0, x1, y1, originX, originY) === Line2Side.RIGHT
    ) {
      continue;
    }

    // check that they have some overlap between them
    const dist = line2PerpDistance(x0, y0, x1, y1, originX, originY);
    const dot0 = Math.min(
      Math.max(ray2DotProject(originX, originY, dirX, dirY, x0, y0), biggerEpsilon),
      currentLen - biggerEpsilon,
    );
    const dot1 = Math.min(
      Math.max(ray2DotProject(originX, originY, dirX, dirY, x1, y1), biggerEpsilon),
      currentLen - biggerEpsilon,
    );
    const diff = dot1 - dot0;
    const absDiff = Math.abs(diff);

    // check for min overlap
    if (absDiff > minOverlap) {
      // add to the list
      ret.push(oppositeWallEdgeNew(i, dot1, dot0, (dot0 + dot1) / 2, dist));
    }
  }

  // sort for near to far
  ret.sort((a, b): number => {
    return a.dist - b.dist;
  });

  let invalidStartT = Number.MAX_VALUE;
  let invalidEndT = -Number.MAX_VALUE;

  // filter function
  const filterOppositeEdges = (a: OppositeWallEdge): boolean => {
    const { startT, endT } = a;

    // check if it is not fully occluded by a previous (closer) edge
    const valid = startT < invalidStartT || endT > invalidEndT;

    const minT = endT > invalidEndT ? invalidEndT : -Number.MAX_VALUE;
    const maxT = endT > invalidEndT ? Number.MAX_VALUE : invalidStartT;

    // update range
    invalidStartT = Math.min(startT - DIST_EPSILON, invalidStartT);
    invalidEndT = Math.max(endT + DIST_EPSILON, invalidEndT);

    // if valid, recalculate the ideal position for the dimension line
    // (to add it to the not occluded part of the edge)
    if (valid) {
      const diff = endT - startT;
      const absDiff = Math.abs(diff);

      a.t = absDiff > idealDimLineOffset ? endT - Math.sign(diff) * idealDimLineOffset : (startT + endT) / 2;
      a.startT = Math.max(startT, minT);
      a.endT = Math.min(endT, maxT);
    }

    return valid;
  };

  // remove the edges fully occluded by others
  return ret.filter(filterOppositeEdges);
}
